import { Controller } from "@hotwired/stimulus"

const POLL_INTERVAL = 3000
const FILTERS = [
  { key: "new", label: "New" },
  { key: "converted", label: "Converted" },
  { key: "dismissed", label: "Dismissed" },
  { key: "rejected", label: "Rejected" }
]

// Drives the "Generate a new search" section: kicks off an agent-run search,
// polls the ProspectSearch record until the job lands it in a terminal status,
// then works the shared candidate inbox underneath.
//
// The table is every candidate across every search, deduped by domain — so it
// stays useful on a cold page load, before you've run anything.
export default class extends Controller {
  static targets = [
    "prompt", "seedUrl", "limit", "runButton", "status", "error",
    "history", "filters", "query", "table", "pageInfo", "scope",
    "prevButton", "nextButton", "convertButton"
  ]

  static values = {
    searchesUrl: String,
    candidatesUrl: String,
    convertUrl: String
  }

  connect() {
    this.filter = "new"
    this.page = 1
    this.searchId = null
    this.scopedPrompt = null
    this.searches = []
    this.counts = {}
    this.selected = new Set()
    this.pollTimer = null
    this.queryTimer = null

    this.renderFilters()
    this.renderScope()
    this.loadHistory()
    this.loadCandidates()
  }

  disconnect() {
    this.stopPolling()
    if (this.queryTimer) clearTimeout(this.queryTimer)
  }

  // --- running a search --------------------------------------------------

  async run() {
    const prompt = this.promptTarget.value.trim()
    if (!prompt) {
      this.showError("Describe who you're looking for.")
      return
    }

    this.clearError()
    this.setRunning(true)

    try {
      const search = await this.postJson(this.searchesUrlValue, {
        prompt,
        seedUrl: this.seedUrlTarget.value,
        limit: this.limitTarget.value
      })
      this.trackSearch(search)
    } catch (error) {
      this.setRunning(false)
      this.showError(error.message)
    }
  }

  // Poll rather than hold a request open: the job takes up to 90s, and polling
  // means a page reload picks the run back up instead of losing it.
  trackSearch(search) {
    this.showStatus(search)

    if (search.finished) {
      this.finishSearch(search)
      return
    }

    this.setRunning(true)
    this.stopPolling()
    this.pollTimer = setTimeout(() => this.pollSearch(search.id), POLL_INTERVAL)
  }

  async pollSearch(id) {
    try {
      const search = await this.getJson(`${this.searchesUrlValue}/${id}`)
      this.trackSearch(search)
    } catch (error) {
      this.setRunning(false)
      this.showError(error.message)
    }
  }

  finishSearch(search) {
    this.stopPolling()
    this.setRunning(false)
    if (search.status === "failed") this.showError(search.error || "The search failed.")

    // Land on what this run actually turned up rather than the whole inbox —
    // the count in the status line and the rows in the table then agree.
    if (search.status === "succeeded") this.scopeToSearch(search)

    this.page = 1
    this.loadHistory()
    this.loadCandidates()
  }

  stopPolling() {
    if (this.pollTimer) clearTimeout(this.pollTimer)
    this.pollTimer = null
  }

  showStatus(search) {
    const target = this.statusTarget
    target.classList.remove("hidden")

    if (search.status === "pending" || search.status === "running") {
      target.className = "mt-3 text-sm text-gray-500"
      // Show elapsed time: a run legitimately takes minutes, and a status line
      // that never changes reads as hung.
      const elapsed = Math.round((Date.now() - new Date(search.createdAt).getTime()) / 1000)
      target.textContent = search.status === "pending"
        ? `Queued — planning the searches… (${elapsed}s)`
        : `Searching the web… usually 1-2 minutes, occasionally longer. (${elapsed}s)`
      return
    }

    if (search.status === "failed") {
      target.className = "mt-3 text-sm text-red-700"
      target.textContent = search.error || "The search failed."
      return
    }

    target.className = "mt-3 text-sm text-emerald-700"
    target.textContent = this.summaryLine(search)
  }

  summaryLine(search) {
    const parts = [`Found ${search.candidatesFound}`, `${search.candidatesNew} new to the inbox`]
    if (search.skippedKnownCount) parts.push(`skipped ${search.skippedKnownCount} you already have`)
    if (search.tokensUsed) parts.push(`${search.tokensUsed.toLocaleString()} tokens`)
    if (search.durationSeconds) parts.push(`${search.durationSeconds}s`)
    return parts.join(" · ")
  }

  // --- search history ----------------------------------------------------

  async loadHistory() {
    try {
      const data = await this.getJson(this.searchesUrlValue)
      this.searches = data.searches || []
      this.renderHistory(this.searches)
      this.renderScope()

      // A run started elsewhere (another tab, another admin) is still ours to
      // watch — the inbox it fills is shared.
      const active = (data.searches || []).find((search) => !search.finished)
      if (active && !this.pollTimer) this.trackSearch(active)
    } catch (error) {
      this.historyTarget.textContent = error.message
    }
  }

  renderHistory(searches) {
    const container = this.historyTarget
    container.innerHTML = ""

    if (!searches.length) {
      container.textContent = "No searches yet."
      return
    }

    const list = document.createElement("ul")
    list.className = "divide-y divide-gray-100"

    searches.forEach((search) => {
      const item = document.createElement("li")
      const active = this.searchId === search.id
      item.className = active
        ? "py-2 pl-2 -ml-2 flex items-start justify-between gap-4 bg-indigo-50 rounded-md"
        : "py-2 pl-2 -ml-2 flex items-start justify-between gap-4"

      // The prompt is the handle: clicking it filters the inbox to this run.
      const left = document.createElement("button")
      left.type = "button"
      left.className = "min-w-0 text-left"
      left.title = active ? "Show all candidates" : "Show only what this search found"
      left.addEventListener("click", () => {
        if (active) this.clearScope()
        else this.scopeToSearch(search)
        this.renderHistory(this.searches)
        this.renderScope()
        this.page = 1
        this.selected.clear()
        this.loadCandidates()
      })

      const prompt = document.createElement("span")
      prompt.className = active
        ? "block text-sm font-semibold text-indigo-900 truncate"
        : "block text-sm text-gray-900 truncate hover:text-indigo-700"
      prompt.textContent = search.prompt
      const meta = document.createElement("span")
      meta.className = "block text-xs text-gray-500"
      meta.textContent = this.historyMeta(search)
      left.append(prompt, meta)

      const rerun = document.createElement("button")
      rerun.type = "button"
      rerun.className = "text-xs font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap"
      rerun.textContent = "Re-run"
      rerun.addEventListener("click", () => {
        this.promptTarget.value = search.prompt
        this.seedUrlTarget.value = search.seedUrl || ""
        this.limitTarget.value = search.limit
        this.promptTarget.scrollIntoView({ behavior: "smooth", block: "center" })
        this.promptTarget.focus()
      })

      item.append(left, rerun)
      list.appendChild(item)
    })

    container.appendChild(list)
  }

  historyMeta(search) {
    const when = new Date(search.createdAt).toLocaleString()
    const by = search.runBy ? ` · ${search.runBy}` : ""

    if (search.status === "failed") return `${when}${by} · failed: ${search.error || "unknown error"}`
    if (!search.finished) return `${when}${by} · ${search.status}…`
    return `${when}${by} · ${this.summaryLine(search)}`
  }

  // --- scoping the inbox to one search ------------------------------------

  scopeToSearch(search) {
    this.searchId = search.id
    // Keep the prompt around: the pill has to name the search even once the
    // run scrolls off the history list.
    this.scopedPrompt = search.prompt
  }

  clearScope() {
    this.searchId = null
    this.scopedPrompt = null
  }

  renderScope() {
    const container = this.scopeTarget
    container.innerHTML = ""

    if (!this.searchId) {
      container.classList.add("hidden")
      return
    }

    container.classList.remove("hidden")

    const pill = document.createElement("div")
    pill.className = "flex items-start gap-2 rounded-md bg-indigo-50 border border-indigo-200 px-3 py-2 text-sm text-indigo-900"

    const label = document.createElement("span")
    label.className = "min-w-0"
    label.textContent = `Showing only candidates from: ${this.scopedPrompt || `search #${this.searchId}`}`

    const clear = document.createElement("button")
    clear.type = "button"
    clear.className = "ml-auto shrink-0 text-xs font-semibold text-indigo-700 hover:text-indigo-900"
    clear.textContent = "Show all"
    clear.addEventListener("click", () => {
      this.clearScope()
      this.renderHistory(this.searches)
      this.renderScope()
      this.page = 1
      this.selected.clear()
      this.loadCandidates()
    })

    pill.append(label, clear)
    container.appendChild(pill)
  }

  // --- candidate inbox ---------------------------------------------------

  renderFilters() {
    const container = this.filtersTarget
    container.innerHTML = ""

    FILTERS.forEach(({ key, label }) => {
      const chip = document.createElement("button")
      chip.type = "button"
      chip.dataset.filter = key
      const active = this.filter === key
      chip.className = active
        ? "rounded-full bg-indigo-600 px-3 py-1 text-xs font-semibold text-white"
        : "rounded-full border border-gray-300 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
      const count = this.counts[key]
      chip.textContent = count === undefined ? label : `${label} (${count})`
      chip.addEventListener("click", () => {
        this.filter = key
        this.page = 1
        this.selected.clear()
        this.renderFilters()
        this.loadCandidates()
      })
      container.appendChild(chip)
    })
  }

  queryChanged() {
    if (this.queryTimer) clearTimeout(this.queryTimer)
    this.queryTimer = setTimeout(() => {
      this.page = 1
      this.loadCandidates()
    }, 250)
  }

  previousPage() {
    if (this.page <= 1) return
    this.page -= 1
    this.loadCandidates()
  }

  nextPage() {
    if (this.page >= this.pages) return
    this.page += 1
    this.loadCandidates()
  }

  async loadCandidates() {
    const params = new URLSearchParams({ filter: this.filter, page: this.page })
    const query = this.hasQueryTarget ? this.queryTarget.value.trim() : ""
    if (query) params.set("q", query)
    if (this.searchId) params.set("searchId", this.searchId)

    try {
      const data = await this.getJson(`${this.candidatesUrlValue}?${params}`)
      this.counts = data.counts || {}
      this.pages = data.pages || 1
      this.page = data.page || 1
      this.renderFilters()
      this.renderTable(data.rows || [])
      this.renderPagination(data)
    } catch (error) {
      this.showError(error.message)
    }
  }

  renderTable(rows) {
    const container = this.tableTarget
    container.innerHTML = ""

    if (!rows.length) {
      container.innerHTML = '<div class="px-4 py-6 text-sm text-gray-500">Nothing here yet.</div>'
      this.updateConvertButton()
      return
    }

    const table = document.createElement("table")
    table.className = "min-w-full text-sm"
    table.innerHTML = `<thead class="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
      <tr>
        <th class="px-4 py-2 w-8"></th>
        <th class="text-left px-4 py-2">Organization</th>
        <th class="text-left px-4 py-2">Score</th>
        <th class="text-left px-4 py-2">Contact</th>
        <th class="text-left px-4 py-2">Evidence</th>
        <th class="text-left px-4 py-2">Found</th>
        <th class="px-4 py-2"></th>
      </tr></thead>`

    const body = document.createElement("tbody")
    body.className = "divide-y divide-gray-100"
    rows.forEach((row) => body.appendChild(this.candidateRow(row)))
    table.appendChild(body)
    container.appendChild(table)

    this.updateConvertButton()
  }

  candidateRow(row) {
    const tr = document.createElement("tr")
    tr.className = row.disqualifiedReason ? "opacity-60" : ""

    tr.append(
      this.selectCell(row),
      this.organizationCell(row),
      this.scoreCell(row),
      this.contactCell(row),
      this.evidenceCell(row),
      this.foundCell(row),
      this.actionsCell(row)
    )
    return tr
  }

  selectCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top"

    // Only pending, qualified rows are convertible — everything else is here to
    // be read, not acted on.
    if (row.state !== "new" || row.disqualifiedReason) return cell

    const box = document.createElement("input")
    box.type = "checkbox"
    box.className = "mt-1 rounded border-gray-300 text-indigo-600"
    box.checked = this.selected.has(row.id)
    box.addEventListener("change", () => {
      if (box.checked) this.selected.add(row.id)
      else this.selected.delete(row.id)
      this.updateConvertButton()
    })
    cell.appendChild(box)
    return cell
  }

  organizationCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top"

    const link = document.createElement("a")
    link.href = row.url
    link.target = "_blank"
    link.rel = "noopener noreferrer"
    link.className = "font-medium text-indigo-600 hover:text-indigo-800"
    link.textContent = row.organization

    const place = document.createElement("div")
    place.className = "text-xs text-gray-500"
    place.textContent = [row.location, row.domain].filter(Boolean).join(" · ")

    cell.append(link, place)

    if (row.state !== "new") {
      const badge = document.createElement("span")
      badge.className = row.state === "converted"
        ? "inline-block mt-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
        : "inline-block mt-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
      badge.textContent = row.state
      cell.appendChild(badge)
    }

    return cell
  }

  scoreCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top whitespace-nowrap"

    const value = document.createElement("div")
    value.className = "font-semibold text-gray-900"
    value.textContent = row.disqualifiedReason ? "—" : `${row.score}/100`

    const components = document.createElement("div")
    components.className = "text-xs text-gray-500 max-w-xs whitespace-normal"
    components.textContent = row.disqualifiedReason
      ? row.disqualifiedReason
      : (row.scoreComponents || []).map((c) => `${c.label} +${c.points}`).join(", ")

    cell.append(value, components)
    return cell
  }

  contactCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top text-xs text-gray-700 whitespace-nowrap"

    if (row.email || row.phone) {
      cell.textContent = [row.email, row.phone].filter(Boolean).join("\n")
      cell.style.whiteSpace = "pre-line"
    } else if (row.enriched) {
      cell.textContent = "None found"
      cell.className += " text-gray-400"
    } else {
      cell.textContent = "—"
      cell.className += " text-gray-400"
    }

    return cell
  }

  evidenceCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top text-gray-700 max-w-md"
    cell.textContent = row.evidence || ""

    if (row.fitRationale) {
      const why = document.createElement("div")
      why.className = "text-xs text-gray-500 mt-1"
      why.textContent = row.fitRationale
      cell.appendChild(why)
    }

    if (row.dismissedReason) {
      const reason = document.createElement("div")
      reason.className = "text-xs text-gray-400 mt-1"
      reason.textContent = `Dismissed: ${row.dismissedReason}`
      cell.appendChild(reason)
    }

    return cell
  }

  foundCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top text-xs text-gray-500 whitespace-nowrap"

    const when = document.createElement("div")
    when.textContent = row.lastSeenAt ? new Date(row.lastSeenAt).toLocaleDateString() : ""

    const times = document.createElement("div")
    times.textContent = row.timesSeen > 1 ? `found by ${row.timesSeen} searches` : ""

    cell.append(when, times)
    return cell
  }

  actionsCell(row) {
    const cell = document.createElement("td")
    cell.className = "px-4 py-3 align-top whitespace-nowrap space-x-3"
    if (row.state !== "new" || row.disqualifiedReason) return cell

    const enrich = document.createElement("button")
    enrich.type = "button"
    enrich.className = "text-xs font-semibold text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
    enrich.textContent = row.enriched ? "Re-enrich" : "Enrich"
    enrich.addEventListener("click", () => this.enrichRow(row, enrich))

    const dismiss = document.createElement("button")
    dismiss.type = "button"
    dismiss.className = "text-xs font-semibold text-gray-500 hover:text-gray-700 disabled:opacity-50"
    dismiss.textContent = "Dismiss"
    dismiss.addEventListener("click", () => this.dismissRow(row, dismiss))

    cell.append(enrich, dismiss)
    return cell
  }

  // Fetches one candidate's site for contacts and needs, and stores what it
  // finds. Only fires on click — the search itself never touches these sites.
  async enrichRow(row, button) {
    button.disabled = true
    button.textContent = "Enriching…"

    try {
      await this.postJson(`${this.candidatesUrlValue}/${row.id}/enrich`, {})
      this.loadCandidates()
    } catch (error) {
      this.showError(error.message)
      button.disabled = false
      button.textContent = "Enrich"
    }
  }

  async dismissRow(row, button) {
    button.disabled = true

    try {
      await this.postJson(`${this.candidatesUrlValue}/${row.id}/dismiss`, {})
      this.selected.delete(row.id)
      this.loadCandidates()
    } catch (error) {
      this.showError(error.message)
      button.disabled = false
    }
  }

  async convertSelected() {
    const ids = Array.from(this.selected)
    if (!ids.length) return

    this.clearError()
    this.convertButtonTarget.disabled = true
    this.convertButtonTarget.textContent = "Converting…"

    try {
      const data = await this.postJson(this.convertUrlValue, { ids })
      const failed = data.failed || []
      if (failed.length) {
        this.showError(`Converted ${(data.committed || []).length}. Failed: ${failed.map((f) => f.error).join("; ")}`)
      }
      this.selected.clear()
      this.loadCandidates()
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.convertButtonTarget.textContent = "Convert selected"
      this.updateConvertButton()
    }
  }

  updateConvertButton() {
    const count = this.selected.size
    this.convertButtonTarget.disabled = count === 0
    this.convertButtonTarget.textContent = count ? `Convert ${count} selected` : "Convert selected"
  }

  renderPagination(data) {
    this.pageInfoTarget.textContent = data.total
      ? `${data.total} candidate${data.total === 1 ? "" : "s"} · page ${data.page} of ${data.pages}`
      : ""
    this.prevButtonTarget.disabled = data.page <= 1
    this.nextButtonTarget.disabled = data.page >= data.pages
  }

  // --- plumbing ----------------------------------------------------------

  async getJson(url) {
    const response = await fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`)
    return data
  }

  async postJson(url, body) {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      credentials: "same-origin",
      body: JSON.stringify(body)
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok) throw new Error(data.error || `Request failed (${response.status})`)
    return data
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  setRunning(running) {
    this.runButtonTarget.disabled = running
    this.runButtonTarget.textContent = running ? "Searching…" : "Run search"
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  clearError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("hidden")
  }
}
